<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useSmarticoMiniGames } from '@/stores/smartico/miniGames';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import SmarticoItemsMiniGame from '@/components/smartico/apostatudo/items/MiniGame.vue';

import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';
import CardBorderWrapper from '@/components/smartico/apostatudo/items/CardBorderWrapper.vue';

const props = defineProps<{
  config: AffiliateConfig;
  iconPath?: string;
  slug?: string;
  isRoletasPage: boolean;
}>();

const gameTypeOrder = [
  'wheel',
  'giftbox',
  'scratch',
  'coin_flip',
  'matchx',
  'quiz',
];

const { miniGames, isLoading: isLoadingMiniGames } = storeToRefs(
  useSmarticoMiniGames(),
);

const { loadMiniGames } = useSmarticoMiniGames();

onMounted(async () => {
  await loadMiniGames();
});

const affiliateMiniGames = computed(() => {
  let normalizedFilters: (string | number)[] = [];

  if (Array.isArray(props.config.smarticoMinigameFilter)) {
    normalizedFilters = props.config.smarticoMinigameFilter.filter(
      (f) => typeof f === 'string' || typeof f === 'number',
    );
  } else if (
    typeof props.config.smarticoMinigameFilter === 'string' ||
    typeof props.config.smarticoMinigameFilter === 'number'
  ) {
    normalizedFilters = [props.config.smarticoMinigameFilter];
  }

  const filteredMiniGames = miniGames.value.filter(
    (minigame: TMiniGameTemplate) => {
      // Condition 1: Always filter out minigame with custom_section_id === 717
      if (minigame.custom_section_id === 717) {
        return false;
      }

      const custom_section_id = minigame.custom_section_id; // Use original value for custom_section_id

      // Check if there are any active filters
      const hasActiveFilters =
        normalizedFilters.length > 0 &&
        !normalizedFilters.every((filter) => filter === '');

      // Condition 2: If no filters are active, include all minigames (except 717, already handled)
      if (!hasActiveFilters) {
        return true;
      }

      // Condition 3a: If filters are active and custom_section_id is falsy (undefined, null, or 0), filter it out.
      if (!custom_section_id) {
        return false;
      }

      // Condition 3b: If filters are active and custom_section_id is defined, apply type-aware filtering
      return normalizedFilters.some((filter) => {
        if (typeof filter === 'string') {
          // If filter is a string, convert custom_section_id to string for case-insensitive comparison
          return String(custom_section_id)
            .toUpperCase()
            .includes(filter.toUpperCase());
        } else if (typeof filter === 'number') {
          // If filter is a number, perform direct numeric comparison
          return custom_section_id === filter;
        }

        return false; // Fallback for unexpected filter types
      });
    },
  );

  // Apply the multi-level sorting logic from MiniGames.vue
  filteredMiniGames.sort((a: TMiniGameTemplate, b: TMiniGameTemplate) => {
    const typeA = a.saw_game_type || 'other';
    const typeB = b.saw_game_type || 'other';

    const orderA = gameTypeOrder.indexOf(typeA);
    const orderB = gameTypeOrder.indexOf(typeB);

    if (orderA !== orderB) {
      return orderA - orderB;
    }

    // Secondary sort by priority if types are the same
    const priorityA = a.saw_template_ui_definition.priority ?? 10;
    const priorityB = b.saw_template_ui_definition.priority ?? 10;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Tertiary sort by price (desc) if priority is also the same
    return (b.buyin_cost_points ?? 0) - (a.buyin_cost_points ?? 0);
  });

  return filteredMiniGames;
});

const dynamicMinigameTitle = computed(() => {
  // This can be extended to support dynamic titles based on filters,
  // similar to MissionsCarousel, if needed in the future.
  return 'Mini Games';
});
</script>

<template>
  <div
    v-if="!isLoadingMiniGames && affiliateMiniGames.length > 0"
    :class="$style.minigamesContainer"
  >
    <PerfilOficialSwiper
      :icon-path="props.iconPath || 'perfil-oficial/minigames'"
      :see-more-url="props.slug ? `${props.slug}` : '/vip/mini-games?tab=all'"
      :title="dynamicMinigameTitle"
    >
      <div
        v-for="minigame in affiliateMiniGames"
        :key="minigame.id"
        class="flex flex-shrink-0 mr-2 w-[320px]"
      >
        <CardBorderWrapper
          v-if="minigame.saw_game_type !== 'matchx'"
          border-width="1px"
          direction="bottom"
          :show-border="props.isRoletasPage"
        >
          <SmarticoItemsMiniGame :minigame="minigame" />
        </CardBorderWrapper>
      </div>
    </PerfilOficialSwiper>
  </div>
</template>

<style module lang="scss">
.minigamesContainer {
  @apply mb-4 md:mb-8;
}
</style>
